/**
 * VoipSetupTab Component
 *
 * Admin tab for a user's SIP/PBX (VoIP) extension setup — the admin side of the
 * CCMS-Chat soft-phone. Shows the current assignment in a table and lets an admin
 * assign / update the extension, password, domain, transport and enabled flag.
 *
 *   GET  /api/users/{id}/sip-credential   -> current setup (never the password)
 *   PUT  /api/users/{id}/sip-credential   -> assign / update
 *
 * The SIP password is stored AES-256-GCM encrypted server-side and is never
 * returned here; leave the password field blank when updating to keep the
 * current one.
 */

import { useState, useEffect, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconPhone,
  IconDeviceFloppy,
  IconAlertCircle,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import {
  Card,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  Alert,
  Text,
  TextInput,
  PasswordInput,
  Select,
  Switch,
  ScrollArea,
  Modal,
  EmptyState,
} from '../../../../components/ui';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { ResponseData } from '../types';

/** Admin-facing view of a user's SIP credential — matches backend SipCredentialView (no password). */
interface SipCredentialView {
  extension: string;
  domain: string;
  transport: string;
  enabled: boolean;
  registeredStatus?: string | null;
}

interface VoipSetupTabProps {
  userId: string;
}

const TRANSPORT_OPTIONS = [
  { value: 'tls', label: 'TLS (recommended)' },
  { value: 'tcp', label: 'TCP' },
  { value: 'udp', label: 'UDP' },
];

export function VoipSetupTab({ userId }: VoipSetupTabProps) {
  const httpClient = useHttpClient();

  const [credential, setCredential] = useState<SipCredentialView | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearConfirmOpened, setClearConfirmOpened] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assign / edit form.
  const [extension, setExtension] = useState('');
  const [password, setPassword] = useState('');
  const [domain, setDomain] = useState('');
  const [transport, setTransport] = useState('tls');
  const [enabled, setEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = getApiUrl('users', `/api/users/${userId}/sip-credential`);
      const response = await httpClient.get<ResponseData<SipCredentialView | null>>(url);
      // The http client may return the envelope wrapped or unwrapped — handle both
      // (same defensive access pattern as ViewUser.fetchUser).
      const cred =
        (((response as any).data?.data ?? (response as any).data) as SipCredentialView | null) ?? null;
      setCredential(cred);
      if (cred) {
        setExtension(cred.extension || '');
        setDomain(cred.domain || '');
        setTransport(cred.transport || 'tls');
        setEnabled(cred.enabled);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load the VoIP setup');
    } finally {
      setLoading(false);
    }
  }, [httpClient, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!extension.trim() || !domain.trim()) {
      setError('Extension and domain (PBX host) are required.');
      return;
    }
    // A new assignment must include a password; an update may leave it blank to keep the current one.
    if (!credential && !password) {
      setError('A SIP password is required when assigning a new extension.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const url = getApiUrl('users', `/api/users/${userId}/sip-credential`);
      await httpClient.put<ResponseData<SipCredentialView>>(url, {
        extension: extension.trim(),
        password: password || undefined, // blank => backend keeps the existing password
        domain: domain.trim(),
        transport,
        enabled,
      });
      notifications.show({ title: 'Saved', message: 'VoIP extension updated.', color: 'green' });
      setPassword('');
      await load();
    } catch (err: any) {
      const message = err?.message || 'Failed to save the VoIP setup';
      setError(message);
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    setError(null);
    try {
      const url = getApiUrl('users', `/api/users/${userId}/sip-credential`);
      await httpClient.delete<ResponseData<boolean>>(url);
      notifications.show({ title: 'Cleared', message: 'VoIP extension unassigned.', color: 'green' });
      // Reset the form back to defaults and reload (now empty).
      setExtension('');
      setPassword('');
      setDomain('');
      setTransport('tls');
      setEnabled(true);
      setClearConfirmOpened(false);
      await load();
    } catch (err: any) {
      const message = err?.message || 'Failed to clear the VoIP setup';
      setError(message);
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      setClearing(false);
    }
  };

  const statusColor = (status?: string | null) => {
    switch ((status || '').toLowerCase()) {
      case 'registered':
        return 'green';
      case 'unregistered':
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Stack gap="md">
      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Error"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Current setup — table */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                VoIP / SIP Extension
              </Text>
              <Text size="xs" c="dimmed">
                The user's PBX extension used by the CCMS-Chat soft-phone
              </Text>
            </div>
            <Group gap="xs">
              <Badge
                size="lg"
                variant="light"
                color={credential ? (credential.enabled ? 'green' : 'gray') : 'gray'}
              >
                {credential ? (credential.enabled ? 'Enabled' : 'Disabled') : 'Not assigned'}
              </Badge>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={load}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
              {credential && (
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => setClearConfirmOpened(true)}
                  size="sm"
                >
                  Clear
                </Button>
              )}
            </Group>
          </Group>

          {credential ? (
            <ScrollArea>
              <Table variant="striped">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Extension</Table.Th>
                    <Table.Th>Domain (PBX host)</Table.Th>
                    <Table.Th>Transport</Table.Th>
                    <Table.Th>Enabled</Table.Th>
                    <Table.Th>Registration</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>
                      <Text fw={500}>{credential.extension || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{credential.domain || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{(credential.transport || '-').toUpperCase()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={credential.enabled ? 'green' : 'gray'} variant="light">
                        {credential.enabled ? 'Yes' : 'No'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(credential.registeredStatus)} variant="light">
                        {credential.registeredStatus || 'Unknown'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconPhone size={64} />}
              title="No extension assigned"
              description="This user does not yet have a SIP/PBX extension. Assign one below."
              size="md"
            />
          )}
        </Stack>
      </Card>

      {/* Assign / edit form */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Text size="lg" fw={600}>
            {credential ? 'Update extension' : 'Assign extension'}
          </Text>
          <Group grow align="flex-start">
            <TextInput
              label="Extension"
              placeholder="e.g. 408"
              value={extension}
              onChange={(e) => setExtension(e.currentTarget.value)}
              required
            />
            <TextInput
              label="Domain (PBX host)"
              placeholder="e.g. 41.66.169.60"
              value={domain}
              onChange={(e) => setDomain(e.currentTarget.value)}
              required
            />
          </Group>
          <Group grow align="flex-start">
            <PasswordInput
              label="SIP password"
              placeholder={credential ? 'Leave blank to keep current' : 'Required'}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              description="Stored encrypted; never shown again."
            />
            <Select
              label="Transport"
              data={TRANSPORT_OPTIONS}
              value={transport}
              onChange={(v) => setTransport(v || 'tls')}
              allowDeselect={false}
            />
          </Group>
          <Switch
            label="Enabled"
            description="When off, the soft-phone treats the extension as not assigned."
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
            >
              {credential ? 'Save changes' : 'Assign extension'}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Clear / unassign confirmation */}
      <Modal
        opened={clearConfirmOpened}
        onClose={() => setClearConfirmOpened(false)}
        title="Unassign VoIP extension"
        centered
      >
        <Stack gap="md">
          <Text>
            Remove this user's SIP/PBX extension{credential?.extension ? ` (${credential.extension})` : ''}?
            They will no longer be able to make or receive calls until a new extension is assigned.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setClearConfirmOpened(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleClear} loading={clearing}>
              Clear extension
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
